import type {ChangeEvent, KeyboardEvent, MouseEvent, ReactElement} from 'react';
import {Text} from '@Components/text';
import type {BrandKitRenameFieldProps} from './brand-kit-rename-field.types';
import {CANCEL_RENAME_KEY, CONFIRM_RENAME_KEY} from './brand-kit-rename-field.types';
import styles from './brand-kit-rename-field.module.scss';

/**
 * A kit's name, or the field that renames it in place: Enter saves, Escape or leaving cancels.
 * It sits inside a clickable row, so none of its clicks or keys may reach that row: a click would select the kit, and the row treats an Enter key-up as a click.
 */
export function BrandKitRenameField({rename, textSize, fieldClassName = ''}: BrandKitRenameFieldProps): ReactElement {
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === CONFIRM_RENAME_KEY) {
      rename.commitRenameFromKeyboard();
      return;
    }

    if (event.key === CANCEL_RENAME_KEY) {
      rename.cancelRenameFromKeyboard();
    }
  };

  const keepEventInsideField = (event: KeyboardEvent | MouseEvent): void => {
    event.stopPropagation();
  };

  if (!rename.isRenaming) {
    return <Text val={rename.displayName} size={textSize} className="-truncated-text" />;
  }

  return (
    <span className={`${styles.nameField} ${fieldClassName}`} data-value={rename.draftName}>
      <input
        ref={rename.inputRef}
        className={styles.nameInput}
        value={rename.draftName}
        aria-label={window.i18next.t('pmwjs_rename')}
        onChange={(event: ChangeEvent<HTMLInputElement>): void => {
          rename.setDraftName(event.target.value);
        }}
        onBlur={rename.cancelRename}
        onKeyDown={onInputKeyDown}
        onKeyUp={keepEventInsideField}
        onClick={keepEventInsideField}
      />
    </span>
  );
}
